import { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import type { EventClickArg, DatesSetArg, EventDropArg, EventContentArg } from '@fullcalendar/core';
import viLocale from '@fullcalendar/core/locales/vi';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import { MailCheck } from 'lucide-react';
import {
  useAuth,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@aaas/shared';
import {
  useCalendarEventsQuery,
  useUpdateCalendarEventMutation,
} from '../../hooks/useCalendarQueries';
import { useMemberRoleListQuery } from '../../hooks/useMemberRoleQueries';
import { CalendarEventFormDialog } from '../../components/CalendarEventFormDialog';
import { CalendarEventDetailDialog } from '../../components/CalendarEventDetailDialog';
import type { CalendarEvent } from '../../types';

const DEFAULT_COLOR = { bg: '#6b7280', border: '#4b5563' };

function mapToFullCalendarEvent(event: CalendarEvent) {
  return {
    id: event.id,
    title: event.title,
    start: event.start_time,
    end: event.end_time,
    backgroundColor: DEFAULT_COLOR.bg,
    borderColor: DEFAULT_COLOR.border,
    extendedProps: { originalEvent: event },
  };
}

export function CalendarPage() {
  const { t, i18n } = useTranslation('ats');
  const { currentWorkspace, idToken } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date().toISOString(),
    end: new Date().toISOString(),
  });

  const { data: memberData } = useMemberRoleListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken ?? undefined,
  });

  const isLeaderOrAbove =
    memberData?.currentUserRole === 'manager' || memberData?.currentUserRole === 'leader';

  const { data: eventsData } = useCalendarEventsQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken ?? undefined,
    startDate: dateRange.start,
    endDate: dateRange.end,
    memberId: selectedMemberId !== 'all' ? selectedMemberId : undefined,
  });

  const events = eventsData?.data?.items ?? [];
  const members = memberData?.roles ?? [];
  const updateMutation = useUpdateCalendarEventMutation();

  const fcEvents = useMemo(() => events.map(mapToFullCalendarEvent), [events]);

  const datesSetTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleDatesSet = useCallback((info: DatesSetArg) => {
    clearTimeout(datesSetTimer.current);
    datesSetTimer.current = setTimeout(() => {
      setDateRange({
        start: info.start.toISOString(),
        end: info.end.toISOString(),
      });
    }, 300);
  }, []);

  const handleEventClick = (info: EventClickArg) => {
    const original = info.event.extendedProps.originalEvent as CalendarEvent;
    if (original) {
      setSelectedEvent(original);
      setIsDetailOpen(true);
    }
  };

  const handleEventDrop = (info: EventDropArg) => {
    const { event } = info;
    if (!event.start || !event.end) return;
    updateMutation.mutate({
      eventId: event.id,
      data: {
        start_time: event.start.toISOString(),
        end_time: event.end.toISOString(),
      },
    });
  };

  const handleEventResize = (info: EventResizeDoneArg) => {
    const { event } = info;
    if (!event.start || !event.end) return;
    updateMutation.mutate({
      eventId: event.id,
      data: {
        start_time: event.start.toISOString(),
        end_time: event.end.toISOString(),
      },
    });
  };

  const handleEditEvent = (event: CalendarEvent) => {
    setEditingEvent(event);
    setIsDetailOpen(false);
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">{t('calendar.pageTitle')}</h1>
        <div className="flex items-center gap-2">
          {isLeaderOrAbove && members.length > 0 && (
            <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('calendar.allMembers')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('calendar.allMembers')}</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.account_id} value={member.account_id}>
                    {`${member.account_name}${member.account_id === memberData?.currentUserAccountId ? t('calendar.meSuffix') : ''}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* FullCalendar */}
      <div className="bg-background rounded-lg border p-4">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          locales={[viLocale]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
          }}
          buttonText={{
            today: t('calendar.today'),
            month: t('calendar.month'),
            week: t('calendar.week'),
            day: t('calendar.day'),
            list: t('calendar.list'),
          }}
          events={fcEvents}
          editable={true}
          eventResizableFromStart={true}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventContent={(arg: EventContentArg) => {
            const e = arg.event.extendedProps.originalEvent as CalendarEvent;
            const isMonthView = arg.view.type === 'dayGridMonth';
            const isListView = arg.view.type.startsWith('list');

            if (isListView) {
              // List view: FullCalendar handles its own columns, just add icon to title
              return (
                <div className="flex items-center gap-1">
                  {e.template_email_sent_at && (
                    <MailCheck className="h-3 w-3 flex-shrink-0 text-green-600" />
                  )}
                  <span>{arg.event.title}</span>
                </div>
              );
            }

            if (isMonthView) {
              // Month (dayGrid) view: replicate fc-daygrid-event-dot + time + title
              return (
                <div className="fc-event-main-frame" style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  <div
                    className="fc-daygrid-event-dot"
                    style={{
                      borderColor: arg.borderColor || arg.backgroundColor,
                    }}
                  />
                  {arg.timeText && (
                    <div className="fc-event-time" style={{ marginRight: 2 }}>
                      {arg.timeText}
                    </div>
                  )}
                  {e.template_email_sent_at && (
                    <MailCheck
                      className="flex-shrink-0 text-green-600"
                      style={{ width: 12, height: 12, marginRight: 2 }}
                    />
                  )}
                  <div className="fc-event-title fc-sticky" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {arg.event.title || '\u00a0'}
                  </div>
                </div>
              );
            }

            // Week / Day (timeGrid) view: time on top, title below
            return (
              <div className="fc-event-main-frame" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
                {arg.timeText && (
                  <div className="fc-event-time">{arg.timeText}</div>
                )}
                <div className="fc-event-title-container" style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  {e.template_email_sent_at && (
                    <MailCheck
                      className="flex-shrink-0 text-green-200"
                      style={{ width: 12, height: 12 }}
                    />
                  )}
                  <div className="fc-event-title fc-sticky" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {arg.event.title || '\u00a0'}
                  </div>
                </div>
              </div>
            );
          }}
          height="calc(100vh - 14rem)"
          nowIndicator={true}
          dayMaxEvents={3}
          locale={i18n.language === 'vi' ? 'vi' : 'en'}
        />
      </div>

      {/* Event Detail Dialog */}
      <CalendarEventDetailDialog
        event={selectedEvent}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onEdit={handleEditEvent}
      />

      {/* Event Form Dialog */}
      <CalendarEventFormDialog
        event={editingEvent}
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
      />
    </div>
  );
}
